import { Core } from "@faire/design-tokens";
import { AspectRatioPicture } from "@faire/retailer-visitor-shared/components/Image/AspectRatioPicture";
import { EAEAEA_PIXEL } from "@faire/retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { ShowroomAnimatedCategoryTile } from "@faire/retailer-visitor-shared/features/Showroom/ShowroomAnimatedCategoryTile/ShowroomAnimatedCategoryTile";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import React from "react";
import styled from "styled-components";

interface IImageSizeInfo {
  height: number;
  width: number;
  ratio?: number;
}

interface IProps {
  keyString?: string;
  link?: string;
  image?: IImage;
  images?: IImage[];
  text?: string;
  parentCategoryName?: string;
  imageSizeInfo: IImageSizeInfo;
  handleStyledLinkOnClick?: () => void;
  dataTestId?: string;
  className?: string;
  useShowroomAnimatedTile?: boolean;
  handleStyledLinkOnHover?: () => void;
  showBorder?: boolean;
  onVisible?: (instance: Element | null | undefined) => void;
}

const ZOOM_IN_SCALE = 1.1;

export const ImageTextLinkTile: React.FC<IProps> = ({
  keyString,
  link,
  image,
  images,
  text,
  imageSizeInfo,
  handleStyledLinkOnClick,
  dataTestId,
  useShowroomAnimatedTile,
  className,
  handleStyledLinkOnHover,
  parentCategoryName,
  showBorder,
  onVisible,
}) => {
  const isSSR = useIsSSR();
  const imageLink = image
    ? optimizeImageUrl(image, isSSR, {
        resizeMethod: "fill",
        ...imageSizeInfo,
      })
    : EAEAEA_PIXEL;

  return (
    <div ref={onVisible}>
      <StyledLink
        key={keyString}
        linkTo={link ?? ""}
        onDragStart={onDragStart}
        onClick={handleStyledLinkOnClick}
        data-test-id={dataTestId}
        className={className}
        onMouseEnter={handleStyledLinkOnHover}
      >
        {useShowroomAnimatedTile && images?.length ? (
          <ShowroomAnimatedCategoryTile images={images} />
        ) : (
          <StyledAspectRatioPicture
            $width="100%"
            $ratio={
              imageSizeInfo.ratio ?? imageSizeInfo.width / imageSizeInfo.height
            }
            $hideBorder={!showBorder}
          >
            <StyledImage
              src={imageLink}
              alt={text}
              style={{
                width: "100%",
                height: "100%",
              }}
              width="100%"
              height="100%"
            />
          </StyledAspectRatioPicture>
        )}
        <Spacer height="1x" />
        {parentCategoryName !== undefined ? (
          <Typography variant="paragraphSansRegular" color={Core.text.subdued}>
            {parentCategoryName}
          </Typography>
        ) : null}
        <StyledTypography>
          <AnimatedUnderlineText>{text ?? ""}</AnimatedUnderlineText>
        </StyledTypography>
      </StyledLink>
    </div>
  );
};

const onDragStart = (e: React.MouseEvent) => e.preventDefault();

const StyledImage = styled.img`
  transition: transform 1200ms cubic-bezier(0.17, 0.67, 0.24, 1);
  transform-origin: left;
`;

const AnimatedUnderlineText = styled.span`
  background-image: linear-gradient(
    ${Core.surface.enabled},
    ${Core.surface.enabled}
  );
  background-size: 0 1px;
  background-position: 0 100%;
  background-repeat: no-repeat;
  transition: background-size 1200ms cubic-bezier(0.17, 0.67, 0.24, 1);
`;

const StyledLink = styled(EmbeddedLink)`
  display: flex;
  text-decoration: none;
  flex-direction: column;
  align-items: flex-start;

  ${responsiveMediaQueries.canHover} {
    &:hover {
      text-decoration: none;
      ${StyledImage} {
        transform: scale(${ZOOM_IN_SCALE});
      }
      ${AnimatedUnderlineText} {
        background-size: 100% 1px;
      }
    }
  }
`;

const StyledAspectRatioPicture = styled(AspectRatioPicture)`
  overflow: hidden;
`;

const StyledTypography = styled(Typography).attrs({
  variant: "paragraphSansRegular",
  color: Core.text.enabled,
})``;

export namespace ImageTextLinkTileStyles {
  export const Text = StyledTypography;
  export const UnderlineText = AnimatedUnderlineText;
  export const Image = StyledImage;
}
